import React, { useState } from 'react';
import styled from 'styled-components';
import { ref, uploadBytes } from 'firebase/storage';
import { storage } from '../../config/firebase';
import { colors, spacing, gradients } from '../../constants/theme';
import { getCurrentUserStoragePath } from '../../utils/domainUtils';

const UploadContainer = styled.div`
  background: ${colors.background.tertiary};
  border: 2px dashed ${colors.background.tertiary};
  border-radius: 16px;
  padding: ${spacing.xxl};
  text-align: center;
  margin-bottom: ${spacing.lg};
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  backdrop-filter: blur(10px);

  &:hover {
    border-color: ${colors.primary.main};
    background: ${colors.primary.main}10;
    transform: translateY(-2px);
  }

  &.dragging {
    border-color: ${colors.primary.main};
    background: ${colors.primary.main}15;
    box-shadow: 0 8px 32px ${colors.primary.main}20;
  }
`;

const UploadIcon = styled.div`
  width: 80px;
  height: 80px;
  background: ${colors.background.primary};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  margin: 0 auto ${spacing.lg};
  border: 2px solid ${colors.background.tertiary};
  transition: all 0.3s ease;

  ${UploadContainer}:hover & {
    border-color: ${colors.primary.main};
    background: ${colors.primary.main}10;
  }
`;

const UploadText = styled.div`
  color: ${colors.text.secondary};
  margin-bottom: ${spacing.lg};
  
  h3 {
    color: ${colors.text.primary};
    margin: 0 0 ${spacing.sm} 0;
    font-size: 20px;
    font-weight: 600;
  }

  p {
    margin: 0 0 ${spacing.sm} 0;
    font-size: 16px;
    line-height: 1.6;
  }

  small {
    font-size: 14px;
    color: ${colors.text.disabled};
  }
`;

const FileInput = styled.input`
  display: none;
`;

const UploadButton = styled.button`
  background: ${gradients.primary};
  color: ${colors.text.primary};
  border: none;
  border-radius: 12px;
  padding: ${spacing.md} ${spacing.xl};
  cursor: pointer;
  font-weight: 600;
  font-size: 16px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 32px ${colors.primary.main}40;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ProgressContainer = styled.div`
  margin-top: ${spacing.lg};
`;

const ProgressBar = styled.div<{ $progress: number }>`
  width: 100%;
  height: 6px;
  background: ${colors.background.primary};
  border-radius: 3px;
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${props => props.$progress}%;
    background: ${gradients.primary};
    transition: width 0.3s ease;
    border-radius: 3px;
  }
`;

const ProgressText = styled.div`
  margin-top: ${spacing.sm};
  font-size: 14px;
  color: ${colors.text.secondary};
  font-weight: 500;
`;

interface FileUploadProps {
  currentPath: string;
  onUploadComplete: () => void;
}

const FileUpload: React.FC<FileUploadProps> = ({ currentPath, onUploadComplete }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);

  const getUserBasePath = (): string => {
    const domainPath = getCurrentUserStoragePath();
    return `flashchecks/${domainPath}`;
  };

  const uploadFile = async (file: File) => {
    try {
      setUploading(true);
      setProgress(0);

      const userBasePath = getUserBasePath();
      const filePath = currentPath ? `${userBasePath}/${currentPath}/${file.name}` : `${userBasePath}/${file.name}`;
      const storageRef = ref(storage, filePath);

      // Upload file
      await uploadBytes(storageRef, file);
      setProgress(100);

      onUploadComplete();
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Error uploading file. Please try again.');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      uploadFile(file);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);
    
    const file = event.dataTransfer.files[0];
    if (file) {
      uploadFile(file);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  return (
    <UploadContainer
      className={dragOver ? 'dragging' : ''}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onClick={() => document.getElementById('file-input')?.click()}
    >
      <UploadIcon>📁</UploadIcon>
      
      <UploadText>
        <h3>Upload Files</h3>
        <p>Drag and drop files here, or click to select files</p>
        <small>Files will be shared with your entire organization</small>
      </UploadText>
      
      <UploadButton disabled={uploading}>
        {uploading ? 'Uploading...' : 'Choose Files'}
      </UploadButton>
      
      {uploading && (
        <ProgressContainer>
          <ProgressBar $progress={progress} />
          <ProgressText>Uploading... {progress}%</ProgressText>
        </ProgressContainer>
      )}
      
      <FileInput
        id="file-input"
        type="file"
        onChange={handleFileSelect}
        accept=".html,.htm,.pdf,.txt,.doc,.docx,.jpg,.png,.gif"
        disabled={uploading}
      />
    </UploadContainer>
  );
};

export default FileUpload;
